import { BreakpointObserver } from '@angular/cdk/layout';
import { NgTemplateOutlet } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { mediaQueries } from '@ecs-helpers';
import { SearchStoreService } from '@ecs-services/search/search-store.service';
import { isSerialNumberResult, SEARCH_TERM_SEPARATOR } from '@ecs-services/search/search.helper';
import { SearchError, SearchResult } from '@ecs-services/search/search.interface';
import { TranslatePipe } from '@ngx-translate/core';
import { arrayEqualsShallow, OnDestroyBase } from '@os-components';
import { SewButton, SewHeadline, SewInput, SewSearchField } from '@sew/ui-components-angular/standalone';
import { distinctUntilChanged, filter, map, take, takeUntil } from 'rxjs';
import { endLoading, startLoading } from 'src/app/helpers/loading/loading';

@Component({
    selector: 'ecs-electronics',
    templateUrl: './electronics.component.html',
    styleUrls: ['./electronics.component.scss'],
    imports: [
        NgTemplateOutlet,
        ReactiveFormsModule,
        FormsModule,
        TranslatePipe,
        SewHeadline,
        SewSearchField,
        SewInput,
        SewButton,
    ],
})
export class ElectronicsComponent extends OnDestroyBase implements OnInit, OnDestroy {
    public isMobile = false;
    public searchValue = '';
    public error: SearchError | null | undefined = null;
    @ViewChild('searchButton', { read: ElementRef })
    private searchButtonRef!: ElementRef<HTMLSewButtonElement>;

    public constructor(
        private breakpointObserver: BreakpointObserver,
        private searchStore: SearchStoreService
    ) {
        super();
    }

    ngOnInit(): void {
        this.searchStore
            .getElectronicsTabSearchTerm()
            .pipe(takeUntil(this.destroySubject))
            .subscribe((searchTerms) => {
                this.searchValue = searchTerms.join(SEARCH_TERM_SEPARATOR);
            });

        this.searchStore
            .getElectronicsTabSearchError()
            .pipe(takeUntil(this.destroySubject))
            .subscribe((error) => {
                this.error = error;
            });

        this.breakpointObserver
            .observe(mediaQueries.mq3)
            .pipe(takeUntil(this.destroySubject))
            .subscribe(({ matches }) => {
                this.isMobile = !matches;
            });
    }

    public searchElectronics() {
        const searchButton = this.searchButtonRef?.nativeElement;
        startLoading(searchButton);
        this.searchStore.electronicsTabSearch(this.searchValue);

        this.searchStore
            .getSearchTermAndErrorsAndResult()
            .pipe(
                filter(
                    ({ searchResult, electronicsTabSearchError }) =>
                        // successfull result
                        this.hasElectronicsSerialNumberResult(searchResult) ||
                        // unsuccessfull result
                        this.hasError(searchResult, electronicsTabSearchError)
                ),
                map(({ searchTerm }) => searchTerm),
                distinctUntilChanged(arrayEqualsShallow),
                take(1)
            )
            .subscribe(() => endLoading(searchButton));
    }

    private hasElectronicsSerialNumberResult = (searchResult: SearchResult | null | undefined): boolean => {
        return isSerialNumberResult(searchResult) && searchResult.isCabinetInverter === true;
    };

    private hasError = (
        searchResult: SearchResult | null | undefined,
        searchError: SearchError | null | undefined
    ): boolean => {
        return !searchResult && !!searchError;
    };

    public resetSearchError() {
        this.error = undefined;
    }
}
